import { useState } from "react";
import { formatDate } from "date-fns";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { InsuranceBadge } from "@/components/shared/badge/insurance.badge";
import { TypeEnvoi } from "@/components/shared/badge/typeEnvoi.badge";
import { ConfirmationModal } from "@/components/shared/confirmation.modal";
import { Incoterm } from "@/components/shared/incoterm";
import { ProtectedInformation } from "@/components/backOffice/updateExpedition/informationDisplay";
import { ConfirmationModalProps } from "@/types/confirmationModal";
import { GetCustomV1BoResponse, GetOneExpeditionBoResponseDto } from "@/types/api/api-types";
import {
  CustomQuotationUpdateBoFormData,
  ExpeditionBoDeliveryInfoFormData,
  QuotationUpdateBoFormData,
} from "@/types/forms/bo/expeditionBo.form";
import { CustomQuotationTypeEnum } from "@/constants/enums/customQuotationType.enum";
import { countries } from "@/constants/countries";
import { CustomPolicyModal } from "../../../customPolicy.modal";
import { useCustomQuotationTypeLabels } from "@/hooks/useEnumLabels";

type SideContentOptionsProps = {
  expedition: GetOneExpeditionBoResponseDto;
  custom?: GetCustomV1BoResponse;
  readOnly?: boolean;
};

const SideContentOptionsShared = ({
  expedition,
  readOnly,
}: {
  expedition: GetOneExpeditionBoResponseDto;
  readOnly: boolean;
}) => {
  const { t } = useTranslation("boExpeditionForm");
  const methods = useFormContext<ExpeditionBoDeliveryInfoFormData>();
  const [confirmationModalState, setConfirmationModalState] =
    useState<ConfirmationModalProps | null>(null);
  const [expressWatch, insuranceWatch, dapOptionWatch, signatureWatch] = useWatch({
    control: methods.control,
    name: ["options.express", "options.insurance", "options.dapOption", "options.signature"],
  });
  const savedOptions = expedition.expeditionOptions;
  const express = readOnly ? savedOptions?.express ?? false : expressWatch;
  const insurance = readOnly ? savedOptions?.insurance ?? false : insuranceWatch;
  const dapOption = readOnly ? savedOptions?.dapOption ?? false : dapOptionWatch ?? false;
  const signature = readOnly ? savedOptions?.signature ?? false : signatureWatch;

  const confirmToggle = (state: ConfirmationModalProps) =>
    readOnly ? undefined : () => setConfirmationModalState(state);

  return (
    <>
      <Separator className="my-2" />
      <span className="text-sm text-gray-500">{t("sidePanelOptions.optionsTitle")}</span>
      <ProtectedInformation
        label={t("sidePanelOptions.shippingTypeLabel")}
        value={<TypeEnvoi express={express} />}
        onClick={confirmToggle({
          title: t("sidePanelOptions.editShippingTypeTitle"),
          text: express
            ? t("sidePanelOptions.expressToStandardText")
            : t("sidePanelOptions.standardToExpressText"),
          confirm: () => methods.setValue("options.express", !express),
        })}
      />
      <ProtectedInformation
        label={t("sidePanelOptions.insuranceLabel")}
        value={<InsuranceBadge insurance={insurance} />}
        onClick={confirmToggle({
          title: t("sidePanelOptions.editInsuranceTitle"),
          text: insurance
            ? t("sidePanelOptions.disableInsuranceText")
            : t("sidePanelOptions.enableInsuranceText"),
          confirm: () => methods.setValue("options.insurance", !insurance),
        })}
      />
      <ProtectedInformation
        label={t("sidePanelOptions.incotermLabel")}
        value={<Incoterm dapOption={dapOption} />}
        onClick={confirmToggle({
          title: t("sidePanelOptions.editIncotermTitle"),
          text: dapOption
            ? t("sidePanelOptions.disableDapText")
            : t("sidePanelOptions.enableDapText"),
          confirm: () => methods.setValue("options.dapOption", !dapOption),
        })}
      />
      <ProtectedInformation
        label={t("sidePanelOptions.signatureLabel")}
        value={<InsuranceBadge insurance={signature} />}
        onClick={confirmToggle({
          title: t("sidePanelOptions.editSignatureTitle"),
          text: signature
            ? t("sidePanelOptions.disableSignatureText")
            : t("sidePanelOptions.enableSignatureText"),
          confirm: () => methods.setValue("options.signature", !signature),
        })}
      />
      {confirmationModalState && (
        <ConfirmationModal
          open={true}
          onClose={() => setConfirmationModalState(null)}
          title={confirmationModalState.title}
          text={confirmationModalState.text}
          confirm={confirmationModalState.confirm}
        />
      )}
    </>
  );
};

const QuotationSideContentOptions = ({
  custom,
  expedition,
  readOnly,
}: {
  custom?: GetCustomV1BoResponse;
  expedition: GetOneExpeditionBoResponseDto;
  readOnly: boolean;
}) => {
  const { t } = useTranslation("boExpeditionForm");
  const methods = useFormContext<QuotationUpdateBoFormData>();
  const [customPolicyModalOpen, setCustomPolicyModalOpen] = useState(false);

  const woodBoxWatch = useWatch({
    control: methods.control,
    name: "quotationWoodBox",
  });
  const woodBoxes = readOnly ? expedition.quotation?.quotationWoodBox : woodBoxWatch;

  const customId = expedition.quotation?.customId;

  return (
    <div className="flex w-full flex-col gap-1">
      <div className="grid grid-cols-2 items-start gap-1">
        <button
          type="button"
          onClick={() => customId && setCustomPolicyModalOpen(true)}
          disabled={!customId}
          className="flex flex-col gap-1 text-left disabled:cursor-default"
        >
          <p className="text-sm text-gray-500">{t("sidePanelOptions.customPolicyLabel")}</p>
          <div className="flex flex-row items-center gap-1">
            {custom?.flagCode && (
              <img
                src={`http://purecatamphetamine.github.io/country-flag-icons/3x2/${custom.flagCode.toUpperCase()}.svg`}
                className="h-4 w-4 rounded-full object-cover"
                alt={custom.label}
              />
            )}
            <p className="text-sm text-gray-900 underline-offset-2 hover:underline">
              {custom?.label}
            </p>
          </div>
        </button>
        <div className="flex flex-col gap-1 place-self-end">
          <p className="text-sm text-gray-500">{t("sidePanelOptions.packagingLabel")}</p>
          {woodBoxes && woodBoxes.length > 0 ? (
            <Badge variant="blue">{t("sidePanelOptions.woodBox")}</Badge>
          ) : (
            <Badge variant="gray">{t("sidePanelOptions.standardPackaging")}</Badge>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm text-gray-500">{t("sidePanelOptions.pickupDateLabel")}</p>
          <p className="text-sm text-gray-900">
            {expedition.pickupInfo?.pickupDate
              ? formatDate(expedition.pickupInfo?.pickupDate, "dd/MM/yyyy")
              : "-"}
          </p>
        </div>
        {expedition.deliveryInfo?.deliveryFromDate && (
          <div className="flex w-fit flex-col gap-1 place-self-end">
            <p className="text-sm text-gray-500">{t("sidePanelOptions.deliveryFromDateLabel")}</p>
            <p className="text-sm text-gray-900">
              {expedition.deliveryInfo?.deliveryFromDate
                ? formatDate(expedition.deliveryInfo?.deliveryFromDate, "dd/MM/yyyy")
                : "-"}
            </p>
          </div>
        )}
      </div>
      <SideContentOptionsShared expedition={expedition} readOnly={readOnly} />
      {customPolicyModalOpen && customId && (
        <CustomPolicyModal
          open={true}
          onClose={() => setCustomPolicyModalOpen(false)}
          customId={customId}
        />
      )}
    </div>
  );
};

const CustomQuotationSideContentOptions = ({
  expedition,
  readOnly,
}: {
  expedition: GetOneExpeditionBoResponseDto;
  readOnly: boolean;
}) => {
  const { t } = useTranslation("boExpeditionForm");
  const customQuotationTypeLabels = useCustomQuotationTypeLabels();
  const methods = useFormContext<CustomQuotationUpdateBoFormData>();
  const [customPolicyModalOpen, setCustomPolicyModalOpen] = useState(false);

  const [typeWatch, countryCodeWatch] = useWatch({
    control: methods.control,
    name: ["type", "deliveryInfo.countryCode"],
  });
  const customQuotationType = ((readOnly ? undefined : typeWatch) ??
    expedition.customQuotation?.type) as CustomQuotationTypeEnum | undefined;
  const destinationCountryCode =
    (readOnly ? undefined : countryCodeWatch) ?? expedition.deliveryInfo?.country ?? "";
  const destinationCountryLabel =
    countries.find((country) => country.countryCode === destinationCountryCode)?.label_fr ?? "-";

  const customId = expedition.customQuotation?.customId;

  return (
    <div className="flex w-full flex-col gap-1">
      <div className="items-centers flex w-full flex-row justify-between">
        <button
          type="button"
          onClick={() => customId && setCustomPolicyModalOpen(true)}
          disabled={!customId}
          className="flex flex-col gap-1 text-left disabled:cursor-default"
        >
          <p className="text-sm text-gray-500">{t("sidePanelOptions.destinationCountryLabel")}</p>
          <div className="flex flex-row items-center gap-1">
            {destinationCountryCode && (
              <img
                src={`http://purecatamphetamine.github.io/country-flag-icons/3x2/${destinationCountryCode.toUpperCase()}.svg`}
                className="h-4 w-4 rounded-full object-cover"
                alt={destinationCountryLabel}
              />
            )}
            <p className="text-sm text-gray-900 underline-offset-2 hover:underline">
              {destinationCountryLabel}
            </p>
          </div>
        </button>
        <div className="flex flex-col gap-1">
          <p className="text-sm text-gray-500">{t("sidePanelOptions.requestTypeLabel")}</p>
          <Badge variant="gray">
            {customQuotationType ? customQuotationTypeLabels[customQuotationType] : "-"}
          </Badge>
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-sm text-gray-500">{t("sidePanelOptions.pickupDateLabel")}</p>
        <p className="text-sm text-gray-900">
          {expedition.pickupInfo?.pickupDate
            ? formatDate(expedition.pickupInfo?.pickupDate, "dd/MM/yyyy")
            : "-"}
        </p>
      </div>
      <SideContentOptionsShared expedition={expedition} readOnly={readOnly} />
      {customPolicyModalOpen && customId && (
        <CustomPolicyModal
          open={true}
          onClose={() => setCustomPolicyModalOpen(false)}
          customId={customId}
        />
      )}
    </div>
  );
};

export const SideContentOptions = ({
  custom,
  expedition,
  readOnly = false,
}: SideContentOptionsProps) => {
  if (!expedition.quotation) {
    return <CustomQuotationSideContentOptions expedition={expedition} readOnly={readOnly} />;
  }

  return (
    <QuotationSideContentOptions custom={custom} expedition={expedition} readOnly={readOnly} />
  );
};
